import type { ReactElement } from 'react';
import { useState } from 'react';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import cx from 'classnames';
import { ExceptionIgnoreScope } from '../../../../shared/issuesV2';
import Locator from '../../../../shared/Locator';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useAddToast } from '../../../contexts/ToastContext';
import { errorMessageFromResponse } from '../../../helpers/errors';
import { renderRevision } from '../../../../shared/helpers/renderLocator';
import { useDispatchOnMount } from '../../../hooks';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { Badge, BadgeColors, BUTTON_SIZE, Icon, icons } from '../../generic';
import DropdownButton from '../../generic/DropdownButton';
import List from '../../generic/List';
import Text from '../../generic/Text';
import Tooltip from '../../generic/Tooltip';
import { canRemoveException } from './permissions';
import type { ExceptionPackageScope, Category, IssueExceptionsFilter, IssueExceptionsPagination } from '../../../../shared/issuesV2';
import type { LocatorParts } from '../../../../shared/Locator';
import type { FailedFetchResponse } from '../../../helpers/errors';
import styles from './IssueExceptionsTable.module.less';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import ListRowLoading from '../../generic/List/ListRowLoading';

// This will have more things in the future, but for now it's just remove.
type ExceptionAction = {
  onSelect: (id: number) => void;
};
type ExceptionActionLabel = 'remove';

const columnNames = [
  'Issue ignored',
  'Package',
  'Version',
  'Scope',
  'By',
  'Note',
];

const scopeToBadgeText: Record<ExceptionIgnoreScope, Uppercase<string>> = {
  [ExceptionIgnoreScope.Project]: 'PROJECT',
  [ExceptionIgnoreScope.ProjectAndReleaseGroups]: 'PROJ + RG',
  [ExceptionIgnoreScope.ReleaseGroup]: 'RELEASE GROUP',
  [ExceptionIgnoreScope.Policy]: 'POLICY',
  [ExceptionIgnoreScope.Organization]: 'GLOBAL',
};

/**
 * Provides a human readable ignore scope
 *
 * @param ignoreScope {ExceptionIgnoreScope} Ignore scope
 * @param projectTitle {string} Project title of the exception
 * @returns {string} Human readable ignore scope
 */
const getHumanReadableIgnoreScope = (
  ignoreScope: ExceptionIgnoreScope,
  projectTitle: string | null,
  policyTitle: string | null,
): string | ReactElement => {
  return (
    <Tooltip id="exception-project-tooltip" tooltip={projectTitle ?? policyTitle}>
      <span>
        <span className={styles.scopeBadge}>
          <Badge fullWidth color={BadgeColors.LightGray}>{scopeToBadgeText[ignoreScope]}</Badge>
        </span>
        {projectTitle ?? policyTitle}
      </span>
    </Tooltip>
  );
};

/**
 * Provides a human readable version, taking into account package scoping
 * @param packageScope {ExceptionPackageScope} Package scoping
 * @param revision {LocatorParts} Revision with version
 * @returns {string} 'All' or human readable version (linux parsed)
 */
const getHumanReadableVersion = (packageScope: ExceptionPackageScope, revision: LocatorParts | null) =>
  packageScope === 'ALL_VERSIONS' || !revision?.revision ? 'All' : renderRevision(revision);

type Props = {
  policyId?: number;
  projectId?: string;
  releaseGroupId?: number;
  category: Category.Licensing | Category.Vulnerability,
};

/**
 * Renders a basic table containing policy exceptions
 */
const IssueExceptionsTable = ({ policyId, projectId, releaseGroupId, category }: Props) => {
  const { onConfirm } = useDialogContext();
  const addToast = useAddToast();
  const [page, setPageState] = useState<number>(1);
  const [count, setCountState] = useState<number>(10);
  useDispatchOnMount(
    IssuesV2Store.actions.fetchIssueExceptions,
    { policyId, projectId, releaseGroupId, category },
    { page, count },
  );

  const dispatch = useDispatch();
  const exceptionsData = useSelector(IssuesV2Store.selectors.getIssueExceptions);
  const totalCount = useSelector(IssuesV2Store.selectors.getIssueExceptionsCount);
  const { isLoading } = useSelector(IssuesV2Store.selectors.getAsyncStatusIssueExceptions);

  const fetchExceptionsData = (filters: IssueExceptionsFilter, pagination: IssueExceptionsPagination) => {
    dispatch(
      IssuesV2Store.actions.fetchIssueExceptions(filters, pagination),
    );
  };

  const setPage = (newPage: number) => {
    setPageState(newPage);
    fetchExceptionsData({ policyId, projectId, releaseGroupId, category }, { page: newPage, count });
  };

  const setCount = (newCount: number) => {
    setCountState(newCount);
    setPageState(1);
    fetchExceptionsData({ policyId, projectId, releaseGroupId, category }, { page: 1, count: newCount });
  };

  const actions: Record<ExceptionActionLabel, ExceptionAction> = {
    remove: {
      onSelect: (id: number) => onConfirm({
        title: 'Remove rule?',
        body: '1 rule will be removed.',
        onConfirm: async () => {
          try {
            // If/when the exceptions table state gets moved into redux, we should move this into a thunk.
            await dispatch(IssuesV2Store.actions.removeIssueException(id));
            fetchExceptionsData({ policyId, projectId, releaseGroupId, category }, { page, count });

            addToast({ content: 'Ignore rule removed.', type: 'success' });
          } catch (error) {
            const message = errorMessageFromResponse(error as FailedFetchResponse);
            addToast({ content: message, type: 'error' });
          }
        },
      }),
    },
  };

  return (
    <>
      <List className={cx(styles.issueExceptionsTable, { [styles.issueExceptionsTable__loading]: isLoading })}>
        <List.Header>
          <List.HeaderText>
            <div className={cx(styles.issueExceptionsTable__header)}>
              {columnNames.map((name) => (
                <span>{name}</span>
              ))}
            </div>
          </List.HeaderText>
        </List.Header>
        <List.Body>
          {isLoading && _.times(count, () => <ListRowLoading />)}
          {!isLoading && exceptionsData.length === 0 && (
            <List.RowContainer>
              <List.Row>
                <div className={cx(styles.issueExceptionsTable__noResults)}>
                  <Text.Heading>No rules found</Text.Heading>
                </div>
              </List.Row>
            </List.RowContainer>
          )}
          {!isLoading &&
            exceptionsData.map((exception) => (
              <List.RowContainer>
                <List.Row>
                  <span className={cx(styles.issueExceptionsTable__row)} key={exception.id}>
                    {/* Issue ignored */}
                    <span className={cx(styles.exceptionColumn)}>{exception.exceptionTitle}</span>

                    {/* Package */}
                    <span className={cx(styles.exceptionColumn)}>{exception.dependencyTitle}</span>

                    {/* Version */}
                    <span className={cx(styles.exceptionColumn)}>
                      {getHumanReadableVersion(
                        exception.packageScope,
                        Locator.parse(exception.dependencyRevisionLocator || ''),
                      )}
                    </span>

                    {/* Scope */}
                    <span className={cx(styles.exceptionColumn)}>
                      {getHumanReadableIgnoreScope(
                        exception.ignoreScope, exception.projectTitle, exception.policyTitle)}
                    </span>

                    {/* By */}
                    <span className={cx(styles.exceptionColumn)}>{exception.createdBy}</span>

                    {/* Note */}
                    <span className={cx(styles.exceptionColumn)}>
                      <Tooltip
                        id="exception-note-tooltip"
                        tooltip={exception.note}
                        showTooltip={!!exception.note}
                        placement="left"
                    >
                        <span>{exception.note}</span>
                      </Tooltip>
                    </span>

                    {/* Action button */}
                    <span className={cx(styles.exceptionColumnButton)}>
                      <DropdownButton
                        id="exception-remove-button"
                        className="exception-actions-button"
                        title={
                          <span style={{ fontSize: '16px' }}>
                            <Icon icon={icons.ellipsisHorizontal} />
                          </span>
                      }
                        size={BUTTON_SIZE.SMALL}
                        onSelect={(label: ExceptionActionLabel) => actions[label].onSelect(exception.id)}
                        noCaret
                    >
                        <DropdownButton.Item
                          key="remove"
                          eventKey="remove"
                          disabled={!canRemoveException(exception)}
                          type="danger"
                      >
                          <Tooltip
                            id="exception-remove-tooltip"
                            showTooltip={!canRemoveException(exception)}
                            tooltip="You do not have permission to remove this exception."
                        >
                            <span>Remove rule</span>
                          </Tooltip>
                        </DropdownButton.Item>
                      </DropdownButton>
                    </span>
                  </span>
                </List.Row>
              </List.RowContainer>
            ))}
        </List.Body>
      </List>
      <PaginationFooter page={page} count={count} totalItems={totalCount} setPage={setPage} setCount={setCount} />
    </>
  );
};

export default IssueExceptionsTable;
